/**
 * localStorage wrapper with lightweight metadata tracking.
 *
 * Every `setItem` / `removeItem` also maintains a sibling key
 * `ghostcomm__meta_{key}` holding JSON `{ createdAt, updatedAt }` ISO
 * timestamps. This powers the Local Data Inspector's
 * "Created X ago · Last touched Y ago" transparency line.
 *
 * The meta key starts with `ghostcomm` too, so the panic button (which wipes
 * every key beginning with `ghostcomm`) cleans the timestamps along with the
 * primary data — no leaked audit trail.
 *
 * All public helpers swallow exceptions so storage quota / privacy-mode errors
 * never cascade into real app flows.
 */

export const META_PREFIX = 'ghostcomm__meta_';

const nowIso = () => new Date().toISOString();

function readMetaRaw(key) {
  try {
    const raw = window.localStorage.getItem(META_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeMeta(key, meta) {
  try {
    window.localStorage.setItem(META_PREFIX + key, JSON.stringify(meta));
  } catch {}
}

/** Write `value` under `key` and bump its metadata timestamps. */
export function setItem(key, value) {
  window.localStorage.setItem(key, value);
  const now = nowIso();
  const existing = readMetaRaw(key);
  writeMeta(key, {
    createdAt: existing?.createdAt || now,
    updatedAt: now,
  });
}

/** Remove `key` and its sibling metadata entry. */
export function removeItem(key) {
  try {
    window.localStorage.removeItem(key);
  } catch {}
  try {
    window.localStorage.removeItem(META_PREFIX + key);
  } catch {}
}

/** Returns `{ createdAt, updatedAt }` for `key`, or `null` if none tracked. */
export function getMeta(key) {
  return readMetaRaw(key);
}

/** True when `k` is itself a metadata sibling key (e.g. ghostcomm__meta_*). */
export function isMetaKey(k) {
  return typeof k === 'string' && k.startsWith(META_PREFIX);
}
